import {
  type AbstractMesh, ArcRotateCamera, Color3, Color4, DirectionalLight, Engine, GlowLayer, HemisphericLight,
  ImageProcessingConfiguration, type Mesh, Scene, ShadowGenerator, Vector3,
} from "@babylonjs/core";
import { FOG, HEMI, SHADOW_FRUSTUM, SKY_STOPS, SUN, shouldGlow, shouldShadow, sunDirection } from "./atmosphere";
import { SceneSwitch } from "./sceneSwitch";
import { createSky } from "./sky";

export interface Atmosphere {
  addShadowCaster(m: AbstractMesh): void;
  removeShadowCaster(m: AbstractMesh): void;
  addGlow(m: Mesh): void;
  follow(x: number, z: number): void;
}

export function createScene(canvas: HTMLCanvasElement): { engine: Engine; scene: Scene; atmosphere: Atmosphere; view: SceneSwitch } {
  const engine = new Engine(canvas, true, { stencil: true, preserveDrawingBuffer: false });
  const scene = new Scene(engine);
  const horizon = Color3.FromHexString(SKY_STOPS[0]!.color);
  scene.clearColor = new Color4(horizon.r, horizon.g, horizon.b, 1);
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogDensity = FOG.density;
  scene.fogColor = Color3.FromHexString(FOG.color);
  scene.collisionsEnabled = true;
  scene.gravity = new Vector3(0, -0.5, 0);

  const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), scene);
  hemi.diffuse = Color3.FromHexString(HEMI.sky);
  hemi.groundColor = Color3.FromHexString(HEMI.ground);
  hemi.intensity = HEMI.intensity;
  const dir = new Vector3(...sunDirection());
  const sun = new DirectionalLight("sun", dir, scene);
  sun.diffuse = Color3.FromHexString(SUN.color);
  sun.intensity = SUN.intensity;
  createSky(scene, SUN.color);

  const ip = scene.imageProcessingConfiguration;
  ip.toneMappingEnabled = true;
  ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  ip.exposure = 1.05;
  ip.contrast = 1.1;
  ip.vignetteEnabled = true;
  ip.vignetteWeight = 0.3;

  const coarse = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  const glow = shouldGlow(coarse) ? new GlowLayer("glow", scene, { mainTextureSamples: 2 }) : null;
  if (glow) glow.intensity = 0.6;

  let shadows: ShadowGenerator | null = null;
  if (shouldShadow(coarse, window.innerWidth, window.devicePixelRatio || 1)) {
    sun.shadowFrustumSize = SHADOW_FRUSTUM;
    sun.autoUpdateExtends = false;
    sun.shadowMinZ = 1;
    sun.shadowMaxZ = 300;
    shadows = new ShadowGenerator(2048, sun);
    shadows.usePercentageCloserFiltering = true;
    shadows.bias = 0.0008;
  }

  const atmosphere: Atmosphere = {
    addShadowCaster: (m) => { shadows?.addShadowCaster(m, true); },
    removeShadowCaster: (m) => { shadows?.removeShadowCaster(m, true); },
    addGlow: (m) => { glow?.addIncludedOnlyMesh(m); },
    follow: (x, z) => { sun.position.set(x - dir.x * 120, -dir.y * 120, z - dir.z * 120); },
  };

  const view = new SceneSwitch(scene);
  engine.runRenderLoop(() => view.frame());
  window.addEventListener("resize", () => engine.resize());
  return { engine, scene, atmosphere, view };
}

/** Slow orbit around the library and plaza for the login screen. Returns a stop function. */
export function startSpectator(scene: Scene): () => void {
  const cam = new ArcRotateCamera("spectator", -Math.PI / 2.6, 1.2, 70, new Vector3(0, 6, 18), scene);
  cam.minZ = 0.5;
  cam.maxZ = 450;
  scene.activeCamera = cam;
  const obs = scene.onBeforeRenderObservable.add(() => {
    cam.alpha += 0.00006 * scene.getEngine().getDeltaTime();
  });
  return () => {
    scene.onBeforeRenderObservable.remove(obs);
    cam.dispose();
  };
}
